import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { useEffect,useMemo,useState } from "react";
import { toast } from "react-toastify";
import { Modal,StaticDropdown,Button,FullScreenLoading,WrappedTimeSeriesChart } from "src/Components";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { CoveredMatrixGroup,CoveredMatrixOrderTableReport,CoveredMatrixState } from "src/Interfaces/coveredMatrixOrder";
import { deleteCoveredMatrixOrder,getCoveredMatrixOrder,setCoveredMatrixOrderActive } from "src/Requests/coveredMatrixOrder";
import { getCCOrderBrokerOrderTable,getCCOrderChancesTable,getCCOrderOrderChart } from "src/Requests/ccOrder";
import { IAdditional,IAdditionalHeaderProps } from "src/Tables/TableTemplate/tableTemplate";
import { TableCell } from "@mui/material";
import { packetErrorHandler } from "src/Utils/requestUtils";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TimelineIcon from "@mui/icons-material/Timeline";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PauseIcon from "@mui/icons-material/Pause";
import { RemoteTable } from "./RemoteTable";
import ImbalanceChecksButton from "src/Tables/CCOrder/ImbalanceChecksButton";

export const MatrixGroupAdditionalCell=({ groupId,orderIds,isActive,state,onAction,onEdit,onGroupChanged,onGroupsDeleted,reportsOnly=false }: {
  groupId: number;
  orderIds: number[];
  isActive: boolean;
  state: CoveredMatrixState;
  onAction: () => void;
  onEdit: () => void;
  onGroupChanged: (groupId: number) => void;
  onGroupsDeleted: (ids: number[]) => void;
  reportsOnly?: boolean;
}) => {
  const [modal,setModal]=useState<"chances"|"orders"|"chart"|null>(null);
  const [loading,setLoading]=useState(false);
  const [modalLoading,setModalLoading]=useState(false);
  const [groupItems,setGroupItems]=useState<CoveredMatrixGroup["items"]>([]);
  const [groupItemsLoaded,setGroupItemsLoaded]=useState(false);
  const [selectedOptionIsin,setSelectedOptionIsin]=useState("");
  const [chartData,setChartData]=useState<Graph_Dto[]>([]);
  const optionFilterItems=useMemo<DropdownItem[]>(() => [
    { id: "",name: "همه آپشن‌های کال" },
    ...groupItems.map((item) => ({
      id: item.optionIsin,
      name: item.optionName||item.optionIsin,
    })),
  ],[groupItems]);
  const filteredOrderIds=useMemo(() => {
    if(!groupItemsLoaded)
      return orderIds;
    if(!selectedOptionIsin)
      return groupItems
        .map((item) => item.coveredCallOrderId)
        .filter((id): id is number => id!=null);
    const selectedItem=groupItems.find((item) => item.optionIsin===selectedOptionIsin);
    return selectedItem?.coveredCallOrderId? [selectedItem.coveredCallOrderId]:[];
  },[groupItems,groupItemsLoaded,selectedOptionIsin,orderIds]);
  const filteredOrderIdsKey=filteredOrderIds.join("|");
  const openModal=(type: "chances"|"orders"|"chart") => {
    setModal(type);
    setSelectedOptionIsin("");
    setGroupItems([]);
    setGroupItemsLoaded(false);
    setChartData([]);
    setModalLoading(true);
    getCoveredMatrixOrder(groupId)
      .then((group) => setGroupItems(group.items||[]))
      .catch(packetErrorHandler)
      .finally(() => {
        setGroupItemsLoaded(true);
        setModalLoading(false);
      });
  };
  useEffect(() => {
    if(modal!=="chart"||!groupItemsLoaded)
      return;
    let disposed=false;
    if(!filteredOrderIds.length) {
      setModalLoading(false);
      setChartData([]);
      return;
    }
    setChartData([]);
    setModalLoading(true);
    Promise.all(filteredOrderIds.map((orderId) => getCCOrderOrderChart(orderId)))
      .then((datasets) => {
        if(!disposed)
          setChartData(datasets.flat());
      })
      .catch(packetErrorHandler)
      .finally(() => {
        if(!disposed)
          setModalLoading(false);
      });
    return () => { disposed=true; };
  },[modal,groupItemsLoaded,filteredOrderIds]);
  const cancel=() => {
    if(loading)
      return;
    setLoading(true);
    deleteCoveredMatrixOrder(groupId)
      .then(() => {
        toast.success("زیرسفارش‌های گروه لغو و گروه حذف شد.");
        onGroupsDeleted([groupId]);
        onAction();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };
  const toggleActive=() => {
    if(loading||state===CoveredMatrixState.Completed||state===CoveredMatrixState.Deleted)
      return;
    setLoading(true);
    setCoveredMatrixOrderActive(groupId,!isActive)
      .then(() => {
        toast.success(isActive? "گروه متوقف شد.":"گروه فعال شد.");
        onGroupChanged(groupId);
        onAction();
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };
  const optionFilter=<div className="mb-3 w-[280px] max-w-full">
    <StaticDropdown
      dropdown={{ name: "callOption",placeholder: "آپشن کال",extraLabel: true }}
      options={optionFilterItems}
      value={selectedOptionIsin}
      onChange={(_,value) => setSelectedOptionIsin(value?.toString()||"")}
    />
  </div>;
  const emptyFilteredResult=<div className="p-8 text-center text-xs text-gray-500">برای آپشن انتخاب‌شده زیرسفارش اجرایی وجود ندارد.</div>;
  return <>
    {loading? <FullScreenLoading />:null}
    {modal==="chances"||modal==="orders"? <Modal
      onClose={() => setModal(null)}
      modalBoxClassName={modal==="chances"? "chance-table-modal":undefined}
      UI={<div className="w-fit max-w-full p-4" dir="rtl">
        {optionFilter}
        {modal === "chances" && filteredOrderIds.length > 0 ? <div className="mb-3"><ImbalanceChecksButton searchParam={{ ids: filteredOrderIds }} /></div> : null}
        {modalLoading? <div className="p-8 text-center text-xs text-gray-500">در حال دریافت اطلاعات…</div>:filteredOrderIds.length? <RemoteTable
          key={`${modal}-${selectedOptionIsin||"all"}-${filteredOrderIdsKey}`}
          title={modal==="chances"? "شانس‌ها":"ریزسفارش‌ها"}
          getData={(offset,pageSize) => modal==="chances"
            ? getCCOrderChancesTable({ ids: filteredOrderIds },offset,pageSize)
            :getCCOrderBrokerOrderTable({ ids: filteredOrderIds },offset,pageSize)}
        />:emptyFilteredResult}
      </div>}
    />:null}
    {modal==="chart"? <Modal
      fullScreen
      title="نمودار"
      modalBoxClassName="covered-call-chart-modal"
      onClose={() => setModal(null)}
      UI={<div className="flex h-full w-full flex-col p-4" dir="rtl">
        {optionFilter}
        <div className="min-h-0 flex-1">
          {modalLoading? <div className="p-8 text-center text-xs text-gray-500">در حال دریافت نمودار…</div>:chartData.length? <WrappedTimeSeriesChart dataset={chartData} className="h-full w-full" />:emptyFilteredResult}
        </div>
      </div>}
    />:null}
    {!reportsOnly? <TableCell onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        title="ویرایش گروه"
        className="flex w-16 mx-auto items-center justify-center"
        onClick={(event) => { event.stopPropagation(); onEdit(); }}
      >
        <EditIcon className="text-sky-700 cursor-pointer" />
      </button>
    </TableCell>:null}
    {!reportsOnly? <TableCell onClick={(event) => event.stopPropagation()}>
      {isActive
        ? <PauseCircleIcon
          titleAccess="توقف گروه"
          onClick={toggleActive}
          className="text-sky-700 cursor-pointer"
        />
        :<PlayCircleIcon
          titleAccess="فعال‌سازی گروه"
          onClick={toggleActive}
          className={state===CoveredMatrixState.Completed? "text-gray-400":"text-sky-700 cursor-pointer"}
        />}
    </TableCell>:null}
    <TableCell onClick={(event) => event.stopPropagation()}>
      <Button
        variant="contained"
        text="شانس‌ها"
        onClick={() => openModal("chances")}
        className="table-btn"
      />
    </TableCell>
    <TableCell onClick={(event) => event.stopPropagation()}>
      <Button
        variant="contained"
        text="سفارشات"
        onClick={() => openModal("orders")}
        className="table-btn"
      />
    </TableCell>
    <TableCell onClick={(event) => event.stopPropagation()}>
      <Button
        variant="contained"
        text={<TimelineIcon className="!text-base" />}
        onClick={() => openModal("chart")}
        className="table-btn"
      />
    </TableCell>
    {!reportsOnly? <TableCell onClick={(event) => event.stopPropagation()}>
      <DeleteIcon fontSize="small" className="text-red-700 cursor-pointer" onClick={cancel} />
    </TableCell>:null}
  </>;
};

export const MatrixGroupAdditionalHeader=({ selectedRowsIds,setSelectedRowsIds,onAction,onGroupChanged,onGroupsDeleted }: IAdditionalHeaderProps&{
  onGroupChanged: (id: number) => void;
  onGroupsDeleted: (ids: number[]) => void;
}) => {
  const [loading,setLoading]=useState(false);
  const enabled=selectedRowsIds.length>0&&!loading;
  const setActive=(isActive: boolean) => {
    if(!enabled)
      return;
    const ids=[...selectedRowsIds];
    setLoading(true);
    Promise.allSettled(ids.map((id) => setCoveredMatrixOrderActive(id,isActive)))
      .then((results) => {
        const succeeded=ids.filter((_,index) => results[index].status==="fulfilled");
        results.forEach((result) => {
          if(result.status==="rejected")
            packetErrorHandler(result.reason);
        });
        if(succeeded.length)
          toast.success(`${succeeded.length} گروه ${isActive? "فعال":"متوقف"} شد.`);
        succeeded.forEach(onGroupChanged);
        setSelectedRowsIds(ids.filter((id) => !succeeded.includes(id)));
      })
      .finally(() => { onAction(); setLoading(false); });
  };
  const removeSelected=() => {
    if(!enabled||!window.confirm(`${selectedRowsIds.length} گروه انتخاب‌شده حذف شوند؟`))
      return;
    const ids=[...selectedRowsIds];
    setLoading(true);
    Promise.allSettled(ids.map(deleteCoveredMatrixOrder))
      .then((results) => {
        const succeeded=ids.filter((_,index) => results[index].status==="fulfilled");
        results.forEach((result) => {
          if(result.status==="rejected")
            packetErrorHandler(result.reason);
        });
        if(succeeded.length) {
          toast.success(`${succeeded.length} گروه حذف شد.`);
          onGroupsDeleted(succeeded);
        }
        setSelectedRowsIds(ids.filter((id) => !succeeded.includes(id)));
      })
      .finally(() => { onAction(); setLoading(false); });
  };
  const actionClass=enabled? "text-sky-900 cursor-pointer":"text-gray-300 cursor-not-allowed";
  return <>
    <TableCell />
    <TableCell>
      <div className="flex items-center justify-center gap-1">
        <PauseIcon
          titleAccess="توقف گروه‌های انتخاب‌شده"
          onClick={() => setActive(false)}
          className={actionClass}
        />
        <PlayCircleIcon
          titleAccess="فعال‌سازی گروه‌های انتخاب‌شده"
          onClick={() => setActive(true)}
          className={actionClass}
        />
      </div>
    </TableCell>
    <TableCell>شانس‌ها</TableCell>
    <TableCell>سفارشات</TableCell>
    <TableCell />
    <TableCell>
      <DeleteIcon
        titleAccess="حذف گروه‌های انتخاب‌شده"
        fontSize="small"
        onClick={removeSelected}
        className={enabled? "text-red-700 cursor-pointer":"text-gray-300 cursor-not-allowed"}
      />
    </TableCell>
  </>;
};

export const getMatrixGroupAdditional=(onEditGroup: (id: number) => void,onGroupChanged: (id: number) => void,onGroupsDeleted: (ids: number[]) => void): IAdditional<CoveredMatrixOrderTableReport> => ({
  getHeader: (props) => <MatrixGroupAdditionalHeader
    {...props}
    onGroupChanged={onGroupChanged}
    onGroupsDeleted={onGroupsDeleted}
  />,
  getRow: ({ row,onAction }) => <MatrixGroupAdditionalCell
    groupId={row.id}
    orderIds={row.coveredCallOrderIds||[]}
    isActive={row.isActive}
    state={row.state}
    onAction={onAction}
    onEdit={() => onEditGroup(row.id)}
    onGroupChanged={onGroupChanged}
    onGroupsDeleted={onGroupsDeleted}
  />,
});

export const matrixTradeAdditional: IAdditional<CoveredMatrixOrderTableReport>={
  getHeader: () => <>
    <TableCell>شانس‌ها</TableCell>
    <TableCell>سفارشات</TableCell>
    <TableCell />
  </>,
  getRow: ({ row,onAction }) => <MatrixGroupAdditionalCell
    groupId={row.id}
    orderIds={row.coveredCallOrderIds||[]}
    isActive={row.isActive}
    state={row.state}
    onAction={onAction}
    onEdit={() => undefined}
    onGroupChanged={() => undefined}
    onGroupsDeleted={() => undefined}
    reportsOnly
  />,
};
